// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.
import * as BABYLON from 'babylonjs';
import Hammer from 'hammerjs';
import * as Enumerations from '../../Enumerations/Enumerations';
import * as Interfaces from '../../Interfaces/Interfaces';
import Collection from '../Structures/Collection';
var eventPrefix: string = "";

class ExamineCamera extends BABYLON.TargetCamera {

    public rotationSensitivityPos: number = 0; // maps to 0.5x..2x
    public velocitySensitivityPos: number = 0; // maps to 0.5x..5x
    public invertY: boolean = false;

    // Zoom scaling
    public pinchFactor = 1;
    public mouseWheelFactor = 1;
    public mouseZoomFactor = 1;
    public speedFactor = 1.25;

    public widthScale = 1;
    public heightScale = 1;
    public inertialAlphaOffset = 0;
    public inertialBetaOffset = 0;
    public inertialRadiusOffset = 0;
    public radiusVelocity = 0;
    public lowerAlphaLimit: any;
    public upperAlphaLimit: any;
    public lowerBetaLimit = 0.01;
    public upperBetaLimit = Math.PI - .01;
    public lowerRadiusLimit: any;
    public upperRadiusLimit: any;
    public _angularSensibilityX = 650.0;
    public _angularSensibilityY = 650.0;
    public wheelPrecision = 3.0;
    public pinchPrecision = 2.0;
    public panningSensibility: number = 1000.0;
    public inertialPanningX: number = 0;
    public inertialPanningY: number = 0;
    public keysUp = [38];
    public keysDown = [40];
    public keysLeft = [37];
    public keysRight = [39];
    public zoomOnFactor = 1;
    public flyZoomFactor = 0;
    public targetScreenOffset = BABYLON.Vector2.Zero();
    public pinchInwards = true;
    public allowUpsideDown = true;
    public panningSpeed = 1;
    public cameraNavigationMode = Enumerations.CameraNavigationMode.Examine;

    public _sceneExtentZoomFactor = 1;
    private _radiusToOrthoScale = 1;
    private _timeSinceFrameUpdate = 0;

    private _keys: any[];
    public _viewMatrix = new BABYLON.Matrix();
    private _cameraAngle = 1.1;
    private _attachedElement: HTMLElement;
    private _mouseOver = false;
    private _clickCount: number = 0;
    private _viewerSelected: boolean = true;

    private _leftClickTime = 0;

    private _moving = false;
    private _modeDirty = false;
    private _goToPosition: any = null;
    private _onContextMenu: (e: PointerEvent) => void;
    private _onClick: (e: PointerEvent) => void;
    private _onPointerDown: (e: PointerEvent) => void;
    private _onPointerUp: (e: PointerEvent) => void;
    private _onPointerMove: (e: PointerEvent) => void;
    private _wheel: (e: BABYLON.IMouseEvent) => void;
    private _onMouseMove: (e: MouseEvent) => any;
    private _onKeyDown: (e: KeyboardEvent) => any;
    private _onKeyUp: (e: KeyboardEvent) => any;
    private _onLostFocus: (e: FocusEvent) => any;
    private _onElementClicked: (e: MouseEvent) => any;
    private _onBodyClicked: (e: MouseEvent) => any;

    private _wheelObs: any;
    private _onMouseLeave: (e: DragEvent) => any;
    private _onMouseEnter: (e: DragEvent) => any;
    declare public _reset: () => void;

    // Panning
    declare private _localDirection: BABYLON.Vector3;
    declare private _transformedDirection: BABYLON.Vector3;
    private _isRightClick: boolean = false;
    private _isCtrlPushed: boolean = false;

    // Collisions
    public onCollide: (collidedMesh: BABYLON.AbstractMesh) => void;
    public checkCollisions = false;
    public collisionRadius = new BABYLON.Vector3(0.5, 0.5, 0.5);

    public zoomFactor: number = 1;
    private _collider = new BABYLON.Collider();
    private _previousPosition = BABYLON.Vector3.Zero();
    private _collisionVelocity = BABYLON.Vector3.Zero();
    private _newPosition = BABYLON.Vector3.Zero();
    private _previousAlpha: number;
    private _previousBeta: number;
    private _previousRadius: number;
    //due to async collision inspection
    private _collisionTriggered: boolean;
    public dollySensitivity = 5;

    private _headlight: BABYLON.DirectionalLight;
    private _eventHandler: Interfaces.Interfaces.IEventManager;

    // When true, user input that would rotate/pan/zoom is ignored (used while gizmos or other UI drag operations are active)
    private _suspendControls: boolean = false;

    public get angularSensibilityX() {
        return this._angularSensibilityX;
    }

    public get angularSensibilityY() {
        return this._angularSensibilityY;
    }

    public set radiusToOrthoScale(newRadius: number) {
        this._radiusToOrthoScale = newRadius;

        this.dollySensitivity = 5 / this._radiusToOrthoScale;
    }

    public get radiusToOrthoScale() {
        return this._radiusToOrthoScale;
    }

    public updateMode(mode: any) {

        if (mode == ExamineCamera.ORTHOGRAPHIC_CAMERA) {
            this.dollySensitivity = 5 / this._radiusToOrthoScale;
        }
        else {
            this.dollySensitivity = 5;
        }

        this.updateViewport();
        this.mode = mode;
    }
    constructor(name: string, eventHander: Interfaces.Interfaces.IEventManager, public alpha: number, public beta: number, public radius: number, target: any, scene: BABYLON.Scene) {
        super(name, BABYLON.Vector3.Zero(), scene);

        eventPrefix = BABYLON.Tools.GetPointerPrefix(scene.getEngine());
        this._eventHandler = eventHander;
        this._goToPosition = null;
        this.alpha = alpha;
        this.beta = beta;
        this.radius = radius;
        this.target = target;
        this.inertialAlphaOffset = 0;
        this.inertialBetaOffset = 0;
        this.inertialRadiusOffset = 0;
        this.lowerAlphaLimit = null;
        this.upperAlphaLimit = null;
        this.lowerBetaLimit = 0.01;
        // There is a bug in the BABYLON.ArcCamera. Setting upperBetaLimit to PI will cause problems when the camera is looking straight up.
        this.upperBetaLimit = Math.PI - .01;
        this.lowerRadiusLimit = null;
        this.upperRadiusLimit = null;
        this.wheelPrecision = 3.0;
        this.pinchPrecision = 2.0;
        this.panningSensibility = 1000.0;
        this.inertialPanningX = 0;
        this.inertialPanningY = 0;
        this.keysUp = [38];
        this.keysDown = [40];
        this.keysLeft = [37];
        this.keysRight = [39];
        this.zoomOnFactor = 1;
        this.targetScreenOffset = BABYLON.Vector2.Zero();
        this.pinchInwards = true;
        this.allowUpsideDown = true;
        this._keys = [];
        this._viewMatrix = new BABYLON.Matrix();
        this._isRightClick = false;
        this._isCtrlPushed = false;
        this.checkCollisions = false;
        this.collisionRadius = new BABYLON.Vector3(0.5, 0.5, 0.5);
        this._collider = new BABYLON.Collider();
        this._previousPosition = BABYLON.Vector3.Zero();
        this._collisionVelocity = BABYLON.Vector3.Zero();
        this._newPosition = BABYLON.Vector3.Zero();
        this.fov = .96;


        if (!this.target) {
            this.target = BABYLON.Vector3.Zero();
        }
        var that = this;
        this._onCollisionPositionChange = function (collisionId, newPosition, collidedMesh) {
            if (collidedMesh === void 0) { collidedMesh = null; }
            if (that.checkCollisions) {
                newPosition.multiplyInPlace(that._collider._radius);
            }
            if (!collidedMesh) {
                that._previousPosition.copyFrom(that.position);
            }
            else {
                that.setPosition(that.position);
                if (that.onCollide) {
                    that.onCollide(collidedMesh);
                }
            }
            // Recompute because of constraints
            var cosa = Math.cos(that.alpha);
            var sina = Math.sin(that.alpha);
            var cosb = Math.cos(that.beta);
            var sinb = Math.sin(that.beta);
            var target = that._getTargetPosition();
            target.addToRef(new BABYLON.Vector3(that.radius * cosa * sinb, that.radius * cosb, that.radius * sina * sinb), that._newPosition);
            that.position.copyFrom(that._newPosition);
            var up = that.upVector;
            if (that.allowUpsideDown && that.beta < 0) {
                var up = up.clone();
                up = up.negate();
            }
            BABYLON.Matrix.LookAtLHToRef(that.position, target, up, that._viewMatrix);

            var newRow = new BABYLON.Vector4(that.targetScreenOffset.x, that.targetScreenOffset.y, that._viewMatrix.m[14], that._viewMatrix.m[15]);
            that.getViewMatrix().setRow(3, newRow);

            that._collisionTriggered = false;
        };
        if (!this.target) {
            this.target = BABYLON.Vector3.Zero();
        }

        this.getViewMatrix();
    }

    public _getTargetPosition(): BABYLON.Vector3 {
        return this.target;
    }

    // Cache
    public _initCache(): void {
        super._initCache();
        this._cache.target = new BABYLON.Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
        this._cache.alpha = undefined;
        this._cache.beta = undefined;
        this._cache.radius = undefined;
        this._cache.targetScreenOffset = undefined;
    }

    public _updateCache(ignoreParentClass?: boolean): void {
        if (!ignoreParentClass) {
            super._updateCache();
        }

        this._cache.target.copyFrom(this._getTargetPosition());
        this._cache.alpha = this.alpha;
        this._cache.beta = this.beta;
        this._cache.radius = this.radius;
        this._cache.targetScreenOffset = this.targetScreenOffset.clone();
    }

    // Synchronized
    public _isSynchronizedViewMatrix(): boolean {
        if (!super._isSynchronizedViewMatrix())
            return false;

        return this._cache.target.equals(this._getTargetPosition())
            && this._cache.alpha === this.alpha
            && this._cache.beta === this.beta
            && this._cache.radius === this.radius
            && this._cache.targetScreenOffset.equals(this.targetScreenOffset);
    }

    // Methods
    public attachControl(element: any, preventDefault?: boolean, useCtrlForPanning: boolean = false): void {
        var that = this;
        var previousPosition: any;
        var previousPan: any;
        var previousPan1: any;
        var previousPan2: any;
        var panCount: any;
        var previousRotate: any;
        element = this.getEngine().getRenderingCanvas() as any;
        if (useCtrlForPanning === void 0) { useCtrlForPanning = false; }
        var cacheSoloPointer: any; // cache pointer object for better perf on camera rotation
        var previousPinchDistance = 0;
        var previousPinchDirection = 0;
        var pointers = new Collection();
        if (that._attachedElement) {
            return;
        }
        that._attachedElement = element;
        var engine = that.getEngine();

        if (that._onPointerDown === undefined) {

            that._onPointerDown = function (evt) {

                if (evt.buttons === 1) {
                    that._leftClickTime = evt.timeStamp;
                }
                else {
                    that._leftClickTime = 0;
                }
                //stop look at position if pointer down event
                that._goToPosition = null;
                // Manage panning with right click
                that._isRightClick = evt.button === 2 ? true : false;
                // manage pointers
                pointers.add(evt.pointerId, { x: evt.clientX, y: evt.clientY, type: evt.pointerType });
                cacheSoloPointer = pointers.item(evt.pointerId);
                if (preventDefault) {
                    evt.preventDefault();
                }
            };
            that._onPointerUp = function (evt) {
                //stop look at position if pointer up event

                //would be better to use pointers.remove(evt.pointerId) for multitouch gestures,
                //but emptying completly pointers collection is required to fix a bug on iPhone :
                //will be ok to put back pointers.remove(evt.pointerId); when iPhone bug corrected
                //pointers.empty();
                if (evt.buttons == 0) {
                    that._goToPosition = null;
                    cacheSoloPointer = null;
                    previousPinchDistance = 0;
                    pointers.remove(evt.pointerId)
                }
                if (preventDefault) {
                    evt.preventDefault();
                }
            };
            that._onContextMenu = function (evt) {
                evt.preventDefault();
            };
            that._onClick = function (evt) {

                if ((evt.timeStamp - that._leftClickTime) < 250) {

                    try {
                        var clickEvent = new MouseEvent("press", { screenX: evt.x, screenY: evt.y });
                    } catch (e) {
                        clickEvent = document.createEvent("MouseEvent");
                        clickEvent.initMouseEvent("press", true, true, window, 0, evt.clientX, evt.clientY, 0, 0, false, false, false, false, 0, evt.target);
                    }

                    window.dispatchEvent(clickEvent);
                }
            };
            that._onPointerMove = function (evt) {
                if (preventDefault) {
                    evt.preventDefault();
                }
                if (evt.pointerType != "touch") {
                    if (that._suspendControls) {
                        // Keep cached pointer position in sync while controls are suspended so resuming
                        // does not apply a large stale delta (prevents a jump when drag ends)
                        if (pointers.item(evt.pointerId)) {
                            var cp = pointers.item(evt.pointerId);
                            cp.x = evt.clientX;
                            cp.y = evt.clientY;
                        }
                        return;
                    }

                    // make sure the evt id is actually remembered as being DOWN! This may not happen if a mouse event is fired when the fingers are dragging!
                    if (pointers.item(evt.pointerId) == null)
                        return;

                    switch (pointers.count) {
                        case 1:

                            if (evt.buttons == 4 || (evt.buttons == 1 && that.cameraNavigationMode == Enumerations.CameraNavigationMode.Pan)) {

                                var minimumRadius = Math.max(that.radius, 1);
                                const velMul = that._velocityMultiplier();
                                that.inertialPanningX += (-(evt.clientX - cacheSoloPointer.x) * velMul) * minimumRadius / that.panningSensibility;
                                that.inertialPanningY += ((evt.clientY - cacheSoloPointer.y) * velMul) * minimumRadius / that.panningSensibility;

                            }
                            else if (evt.buttons == 1) {
                                var offsetX = evt.clientX - cacheSoloPointer.x;
                                var offsetY = evt.clientY - cacheSoloPointer.y;

                                const rotMul = that._rotationMultiplier();
                                const y = that.invertY ? -offsetY : offsetY;

                                // Multiply numerator (equivalent to dividing sensibility)
                                that.inertialAlphaOffset -= (offsetX * rotMul) / that.angularSensibilityX;
                                that.inertialBetaOffset -= (y * rotMul) / that.angularSensibilityY;
                            }
                            else if (evt.buttons == 5) {
                                var offsetY = evt.clientY - cacheSoloPointer.y;
                                var zoomFactor = Math.log(that.radius) / Math.log(20);
                                zoomFactor = zoomFactor < .05 ? .05 : zoomFactor;

                                const velMul = that._velocityMultiplier();
                                that.inertialRadiusOffset += zoomFactor * ((offsetY * velMul) / that.dollySensitivity);
                                that.inertialRadiusOffset *= that.mouseZoomFactor;
                                that.inertialRadiusOffset *= that.speedFactor;
                            }
                            cacheSoloPointer.x = evt.clientX;
                            cacheSoloPointer.y = evt.clientY;

                            break;
                        case 2:
                            pointers.item(evt.pointerId).x = evt.clientX;
                            pointers.item(evt.pointerId).y = evt.clientY;
                            var direction = that.pinchInwards ? -1 : 1;
                            var distX = pointers.getItemByIndex(0).x - pointers.getItemByIndex(1).x;
                            var distY = pointers.getItemByIndex(0).y - pointers.getItemByIndex(1).y;
                            var pinchSquaredDistance = (distX * distX) + (distY * distY);

                            if (previousPinchDistance === 0) {
                                previousPinchDistance = pinchSquaredDistance;
                                return;
                            }
                            if (pinchSquaredDistance !== previousPinchDistance) {

                                var zoomFactor = Math.log(that.radius) / Math.log(20);
                                zoomFactor = zoomFactor < .05 ? .05 : zoomFactor;
                                that.inertialRadiusOffset += zoomFactor * (pinchSquaredDistance - previousPinchDistance) / (that.pinchPrecision * that.wheelPrecision * Math.max(that.angularSensibilityX, that.angularSensibilityY) * direction);
                                that.inertialRadiusOffset *= that.pinchFactor;
                                that.inertialRadiusOffset *= that.speedFactor;
                                const velMul = that._velocityMultiplier();
                                that.inertialRadiusOffset *= velMul;
                                previousPinchDistance = pinchSquaredDistance;
                            }
                            break;
                        default:
                            if (pointers.item(evt.pointerId)) {
                                pointers.item(evt.pointerId).x = evt.clientX;
                                pointers.item(evt.pointerId).y = evt.clientY;
                            }
                    }
                }
            };


            that._onMouseMove = function (evt) {

                if (!engine.isPointerLock) {
                    return;
                }
                if (that._suspendControls) {
                    return;
                }
                var offsetX = evt.movementX || evt.mozMovementX || evt.webkitMovementX || evt.msMovementX || 0;
                var offsetY = evt.movementY || evt.mozMovementY || evt.webkitMovementY || evt.msMovementY || 0;
                const rotMul = that._rotationMultiplier();
                const y = that.invertY ? -offsetY : offsetY;

                that.inertialAlphaOffset -= (offsetX * rotMul) / that.angularSensibilityX;
                that.inertialBetaOffset -= (y * rotMul) / that.angularSensibilityY;

                if (preventDefault) {
                    evt.preventDefault();
                }
            };
            that._wheel = function (event) {
                if (that._suspendControls) {
                    return; // ignore wheel while suspended
                }
                var delta = 0;
                var wheelDelta: number = (event as any).wheelDelta;
                if (wheelDelta) {
                    delta = wheelDelta / (that.wheelPrecision * 40);
                }
                else if (event.detail) {
                    delta = -event.detail / that.wheelPrecision;
                }
                if (delta) {
                    var zoomFactor = Math.log(that.radius) / Math.log(20);
                    zoomFactor = zoomFactor < .05 ? .05 : zoomFactor;
                    const velMul = that._velocityMultiplier();
                    that.inertialRadiusOffset += ((delta * velMul) / that.dollySensitivity) * zoomFactor;
                    that.inertialRadiusOffset *= that.mouseWheelFactor;
                    that.inertialRadiusOffset *= that.speedFactor;
                }
                if (event.preventDefault) {
                    if (preventDefault) {
                        event.preventDefault();
                    }
                }
            };
            that._onKeyDown = function (evt) {

                if (that._viewerSelected) {

                }
            };
            that._onMouseLeave = function (evt) {
                evt.preventDefault();
                that._mouseOver = false;
            };
            that._onMouseEnter = function (evt) {
                that._mouseOver = true;
            };
            that._onKeyUp = function (evt) {
                that._isCtrlPushed = evt.ctrlKey;
                if (that.keysUp.indexOf(evt.keyCode) !== -1 ||
                    that.keysDown.indexOf(evt.keyCode) !== -1 ||
                    that.keysLeft.indexOf(evt.keyCode) !== -1 ||
                    that.keysRight.indexOf(evt.keyCode) !== -1) {
                    var index = that._keys.indexOf(evt.keyCode);
                    if (index >= 0) {
                        that._keys.splice(index, 1);
                    }
                    if (evt.preventDefault) {
                        if (preventDefault) {
                            evt.preventDefault();
                        }
                    }
                }
            };

            that._onElementClicked = function () {

                that._clickCount = 1;
            };

            that._onBodyClicked = function (evt) {
                that._clickCount++;

                // if click == 2, means you have clicked on viewer
                // if click == 1, means you have clicked not on viewer
                if (that._clickCount == 2) {
                    that._viewerSelected = true;
                    evt.preventDefault();
                }
                else {
                    that._viewerSelected = false;
                }

                that._clickCount = 0;
            }

            that._onLostFocus = function () {

                that._keys = [];
                pointers.empty();
                previousPinchDistance = 0;
                cacheSoloPointer = null;
            };

            that._reset = function () {
                that._keys = [];
                that.inertialAlphaOffset = 0;
                that.inertialBetaOffset = 0;
                that.inertialRadiusOffset = 0;
                that.radiusVelocity = 0;
                pointers.empty();
                previousPinchDistance = 0;
                cacheSoloPointer = null;
            };
        }
        if (!useCtrlForPanning) {
            element.addEventListener("contextmenu", that._onContextMenu, false);
        }
        
        var mc = new Hammer.Manager(element, {
            // Disable default recognizers
            recognizers: [
                // Add the pan recognizer with the required minimum number of touches
                [Hammer.Pan, { event: 'pan', pointers: 1 }] // Change this number to the desired minimum touches
            ]
        });

        mc.get('pan').set({ direction: Hammer.DIRECTION_ALL, threshold: 1, pointers: 0 });

        // We do NOT use this pinch setting, but instead detect pinches in our custom implementation of panmove. This is done because hammer's pinch
        // sometimes blocks, and can cause a bit of jumping. There was no noticable difference in the panmove implementation, compared to the
        // pinchin pinchout, so therefore, it seems better to do pinching in panmove.
        //mc.get('pinch').set({ enable: true });

        //listen to events...
        mc.on("panstart pinchstart", function (ev) {

            previousPinchDistance = 0;
            panCount = ev.pointers.length;
            that._moving = true;
            if (that._suspendControls) {
                return;
            }
            if (ev.pointerType == "touch") {
                if (ev.pointers.length == 1 && that.cameraNavigationMode == Enumerations.CameraNavigationMode.Examine) {
                    previousRotate = ev.pointers[0];
                }
                else if (ev.pointers.length == 2 && that.cameraNavigationMode == Enumerations.CameraNavigationMode.Examine) {

                    previousPan = ev.pointers[0];
                    previousPan1 = ev.pointers[0];
                    previousPan2 = ev.pointers[1];
                }
                else if (that.cameraNavigationMode == Enumerations.CameraNavigationMode.Pan) {
                    previousPan = ev.pointers[0];
                    previousPan1 = ev.pointers[0];
                    previousPan2 = ev.pointers[1];
                }
            }
        });
        mc.on("panend", function (ev) {

            that._moving = false;
        });

        mc.on("pinchend", function (ev) {

            that._moving = false;
        });

        // Keep in mind, there is pinch AND pan detection here. There are some tolerance considerations so that pinching is not confused with panning, and vice versa. 
        mc.on("panmove pinchmove", function (ev) {

            if (preventDefault) {
                ev.preventDefault();
            }
            if (that._suspendControls) {
                return; // ignore gesture operations while suspended
            }

            if (ev.pointerType == "touch") {

                if (ev.pointers.length == 1 && that.cameraNavigationMode == Enumerations.CameraNavigationMode.Examine) {

                    if (panCount != ev.pointers.length) {
                        previousRotate = ev.pointers[0];
                        panCount = ev.pointers.length;
                    }

                    var offsetX = ev.pointers[0].clientX - previousRotate.clientX;
                    var offsetY = ev.pointers[0].clientY - previousRotate.clientY;

                    const rotMul = that._rotationMultiplier();
                    const y = that.invertY ? -offsetY : offsetY;

                    that.inertialAlphaOffset -= (offsetX * rotMul) / 300;
                    that.inertialBetaOffset -= (y * rotMul) / 300;

                    previousRotate = ev.pointers[0];
                }
                else if (ev.pointers.length == 2 && that.cameraNavigationMode == Enumerations.CameraNavigationMode.Examine) {

                    if (panCount != ev.pointers.length) {
                        previousPan = ev.pointers[0];
                        previousPan1 = ev.pointers[0];
                        previousPan2 = ev.pointers[1];
                        panCount = ev.pointers.length;
                    }

                    var currentXMiddle = (ev.pointers[0].clientX + ev.pointers[1].clientX) / 2;
                    var currentYMiddle = (ev.pointers[0].clientY + ev.pointers[1].clientY) / 2;


                    var yPanChange = (ev.pointers[0].clientY - previousPan1.clientY) + (ev.pointers[1].clientY - previousPan2.clientY);
                    var xPanChange = (ev.pointers[0].clientX - previousPan1.clientX) + (ev.pointers[1].clientX - previousPan2.clientX);

                    var xPanning = (ev.pointers[0].clientX - previousPan1.clientX) < 0 && (ev.pointers[1].clientX - previousPan2.clientX) < 0 ||
                        (ev.pointers[0].clientX - previousPan1.clientX) > 0 && (ev.pointers[1].clientX - previousPan2.clientX) > 0;

                    var yPanning = (ev.pointers[0].clientY - previousPan1.clientY) < 0 && (ev.pointers[1].clientY - previousPan2.clientY) < 0 ||
                        (ev.pointers[0].clientY - previousPan1.clientY) > 0 && (ev.pointers[1].clientY - previousPan2.clientY) > 0;

                    // Very small changes in the finger difference should be ignored so we do not pan while pinching.
                    if ((xPanChange > 1 || xPanChange < 1) && xPanning) {
                        var minimumRadius = Math.max(that.radius, 1);
                        const velMul = that._velocityMultiplier();
                        that.inertialPanningX += (-(xPanChange / 2) * velMul) * minimumRadius / that.panningSensibility;
                        
                        previousPan = ev.pointers[0];
                        previousPan1 = ev.pointers[0];
                        previousPan2 = ev.pointers[1];
                    }
                    if ((yPanChange > 2 || yPanChange < 2) && yPanning) {
                        var minimumRadius = Math.max(that.radius, 1);
                        

                        const velMul = that._velocityMultiplier();
                        
                        that.inertialPanningY += ((yPanChange / 2) * velMul) * minimumRadius / that.panningSensibility;

                        previousPan = ev.pointers[0];
                        previousPan1 = ev.pointers[0];
                        previousPan2 = ev.pointers[1];
                    }

                    var direction = that.pinchInwards ? 1 : -1;
                    var distX = ev.pointers[0].clientX - ev.pointers[1].clientX;
                    var distY = ev.pointers[0].clientY - ev.pointers[1].clientY;

                    var pinchSquaredDistance = (distX * distX) + (distY * distY);

                    if (previousPinchDistance === 0) {
                        previousPinchDistance = pinchSquaredDistance;
                        return;
                    }

                    if (pinchSquaredDistance !== previousPinchDistance) {

                        // There are two tests for a pinch threshold. 

                        // Firstly is a change of direction. Without this direction change tolerance, we will see jiration in the pinching.
                        // We say that pinching in one direction should make a change in direction a little harder, so that we don't see a jiggling in the camera.
                        var pinchThreshold = ((previousPinchDirection > 0 && (pinchSquaredDistance - previousPinchDistance) > 0) ||
                            (previousPinchDirection < 0 && (pinchSquaredDistance - previousPinchDistance) < 0)) ||
                            previousPinchDirection == 0 ? 1 : 35;

                        // The second threshold test is if we are panning. In this case, pinching should be a little bit harder so that we can keep the camera
                        // steady while panning. Without this line, it is almost impossible to pan without zooming.
                        pinchThreshold = (that.inertialPanningX > .1 || that.inertialPanningY > .1) ? 35 : pinchThreshold;

                        // We use a logrithmic function to scale the dolly speed according to the radius. log 20 was chosed purely from observed "pleasant" results.
                        var pinchSpeed = Math.log(that.radius) / Math.log(20);

                        pinchSpeed = pinchSpeed < .05 ? .05 : pinchSpeed;

                        if (Math.abs((Math.sqrt(pinchSquaredDistance) - Math.sqrt(previousPinchDistance))) > pinchThreshold) {

                            const velMul = that._velocityMultiplier();
                            that.inertialRadiusOffset += pinchSpeed * velMul * ((pinchSquaredDistance - previousPinchDistance) / pinchThreshold) / (that.wheelPrecision * Math.max(that.angularSensibilityX, that.angularSensibilityY) * direction);
                            that.inertialRadiusOffset *= that.pinchFactor;
                            that.inertialRadiusOffset *= that.speedFactor;
                            previousPinchDirection = (pinchSquaredDistance - previousPinchDistance);
                            previousPinchDistance = pinchSquaredDistance;
                        }
                    }
                }
                else if (that.cameraNavigationMode == Enumerations.CameraNavigationMode.Pan) {
                    if (panCount != ev.pointers.length) {
                        previousPan = ev.pointers[0];
                        previousPan1 = ev.pointers[0];
                        previousPan2 = ev.pointers[1];
                        panCount = ev.pointers.length;
                    }

                    var minimumRadius = Math.max(that.radius, 1);

                    that.inertialPanningX += -(ev.pointers[0].clientX - previousPan.clientX) * minimumRadius / that.panningSensibility;
                    that.inertialPanningY += (ev.pointers[0].clientY - previousPan.clientY) * minimumRadius / that.panningSensibility;

                    previousPan = ev.pointers[0];
                    previousPan1 = ev.pointers[0];
                    previousPan2 = ev.pointers[1];

                    if (ev.pointers.length == 2) {
                        var direction = that.pinchInwards ? 1 : -1;
                        var distX = ev.pointers[0].clientX - ev.pointers[1].clientX;
                        var distY = ev.pointers[0].clientY - ev.pointers[1].clientY;

                        var pinchSquaredDistance = (distX * distX) + (distY * distY);

                        if (previousPinchDistance === 0) {
                            previousPinchDistance = pinchSquaredDistance;
                            return;
                        }
                        if (pinchSquaredDistance !== previousPinchDistance) {
                            var pinchThreshold = ((previousPinchDirection > 0 && (pinchSquaredDistance - previousPinchDistance) > 0) ||
                                (previousPinchDirection < 0 && (pinchSquaredDistance - previousPinchDistance) < 0)) ||
                                previousPinchDirection == 0 ? 1 : 35;

                            pinchThreshold = (that.inertialPanningX > .1 || that.inertialPanningY > .1) ? 35 : pinchThreshold;

                            var pinchSpeed = Math.log(that.radius) / Math.log(20);

                            pinchSpeed = pinchSpeed < .05 ? .05 : pinchSpeed;

                            if (Math.abs((Math.sqrt(pinchSquaredDistance) - Math.sqrt(previousPinchDistance))) > pinchThreshold) {

                                that.inertialRadiusOffset += pinchSpeed * ((pinchSquaredDistance - previousPinchDistance) / pinchThreshold) / (that.wheelPrecision * Math.max(that.angularSensibilityX, that.angularSensibilityY) * direction);
                                that.inertialRadiusOffset *= that.pinchFactor;
                                const velMul = that._velocityMultiplier();
                                that.inertialRadiusOffset *= velMul;
                                that.inertialRadiusOffset *= that.speedFactor;
                                previousPinchDirection = (pinchSquaredDistance - previousPinchDistance);
                                previousPinchDistance = pinchSquaredDistance;
                            }
                        }
                    }
                }
            }
        });

        element.addEventListener(eventPrefix + "down", that._onPointerDown, false);
        element.addEventListener(eventPrefix + "up", that._onPointerUp, false);
        element.addEventListener(eventPrefix + "out", that._onPointerUp, false);
        element.addEventListener(eventPrefix + "move", that._onPointerMove, false);
        element.addEventListener("mousemove", that._onMouseMove, false);

        element.addEventListener("click", that._onClick, false);

        that._wheelObs = that._scene.onPointerObservable.add((eventData) => {
            that._wheel(eventData.event);
        }, BABYLON.PointerEventTypes.POINTERWHEEL);

        element.addEventListener("keydown", that._onKeyDown, false);
        element.addEventListener('click', that._onElementClicked, false);
        document.body.addEventListener("click", that._onBodyClicked, false);
        /*
        BABYLON.Tools.RegisterTopRootEvents([
            { name: "keydown", handler: that._onKeyDown },
            { name: "keyup", handler: that._onKeyUp },
            { name: "blur", handler: that._onLostFocus },
        ]);
        */
    }

    public detachControl(element?: any): void {
        if (this._attachedElement !== element) {
            return;
        }
        element.removeEventListener("contextmenu", this._onContextMenu);
        element.removeEventListener("click", this._onClick);
        element.removeEventListener(eventPrefix + "down", this._onPointerDown);
        element.removeEventListener(eventPrefix + "up", this._onPointerUp);
        element.removeEventListener(eventPrefix + "out", this._onPointerUp);
        element.removeEventListener(eventPrefix + "move", this._onPointerMove);
        element.removeEventListener("mousemove", this._onMouseMove);
        element.removeEventListener("keydown", this._onKeyDown, false);

        this._scene.onPointerObservable.remove(this._wheelObs);

        element.removeEventListener('click', this._onElementClicked, false);
        document.body.removeEventListener("click", this._onBodyClicked, false);
        /*
        BABYLON.Tools.UnregisterTopRootEvents([
            { name: "keydown", handler: this._onKeyDown },
            { name: "keyup", handler: this._onKeyUp },
            { name: "blur", handler: this._onLostFocus }
        ]);
        */

        this._attachedElement = null;
        if (this._reset) {
            this._reset();
        }
    }
    public disposeHeadlights() {
        if (this._headlight) {
            this._scene.removeLight(this._headlight);
            this._headlight.dispose();
            this._headlight = null;
        }
    }

    public hasHeadlights() {
        return this._headlight;
    }

    public setHeadlight(light: BABYLON.DirectionalLight) {
        if (this._headlight)
            this._headlight.dispose();

        light.position = this.position;
        this._headlight = light;
    }

    public _checkInputs(): void {

        if (this._collisionTriggered) {
            return;
        }
        // Keyboard
        for (var index = 0; index < this._keys.length; index++) {
            var keyCode = this._keys[index];
            if (this.keysLeft.indexOf(keyCode) !== -1) {
                this.inertialAlphaOffset -= 0.01;
            }
            else if (this.keysUp.indexOf(keyCode) !== -1) {
                this.inertialBetaOffset -= 0.01;
            }
            else if (this.keysRight.indexOf(keyCode) !== -1) {
                this.inertialAlphaOffset += 0.01;
            }
            else if (this.keysDown.indexOf(keyCode) !== -1) {
                this.inertialBetaOffset += 0.01;
            }
        }

        // Inertia
        if (this.inertialAlphaOffset !== 0 || this.inertialBetaOffset !== 0 || this.inertialRadiusOffset != 0 || this._modeDirty || this.radiusVelocity != 0) {
            this.alpha += this.beta <= 0 ? -this.inertialAlphaOffset : this.inertialAlphaOffset;
            this.beta += this.inertialBetaOffset;

            this.updateViewport();

            this.inertialAlphaOffset *= this.inertia;
            this.inertialBetaOffset *= this.inertia;
            this.inertialRadiusOffset *= this.inertia;

            if (Math.abs(this.inertialAlphaOffset) < BABYLON.Epsilon)
                this.inertialAlphaOffset = 0;
            if (Math.abs(this.inertialBetaOffset) < BABYLON.Epsilon)
                this.inertialBetaOffset = 0;
            if (Math.abs(this.inertialRadiusOffset) < BABYLON.Epsilon)
                this.inertialRadiusOffset = 0;

            this._modeDirty = false;
        }
        // Panning inertia
        if (this.inertialPanningX !== 0 || this.inertialPanningY !== 0) {
            if (!this._localDirection) {
                this._localDirection = BABYLON.Vector3.Zero();
                this._transformedDirection = BABYLON.Vector3.Zero();
            }
            this.inertialPanningX *= this.panningSpeed;
            this.inertialPanningY *= (this.panningSpeed + .05);
            if (Math.abs(this.inertialPanningX) < BABYLON.Epsilon)
                this.inertialPanningX = 0;

            if (Math.abs(this.inertialPanningY) < BABYLON.Epsilon)
                this.inertialPanningY = 0;

            this._localDirection.copyFromFloats(this.inertialPanningX, this.inertialPanningY, 0);
            this._viewMatrix.invertToRef(this._cameraTransformMatrix);
            BABYLON.Vector3.TransformNormalToRef(this._localDirection, this._cameraTransformMatrix, this._transformedDirection);
            this.target.addInPlace(this._transformedDirection);

            this.inertialPanningX = 0;
            this.inertialPanningY = 0;
        }
        if (this._goToPosition !== null) {
            if (BABYLON.Vector3.Distance(this._goToPosition, this.target) < BABYLON.Epsilon) {
                this._goToPosition = null;
            }
            else {
                this.target = BABYLON.Vector3.Lerp(this.target, this._goToPosition, this.inertia / 10);
            }
        }
        // Limits
        this._checkLimits();
        super._checkInputs.call(this);

        this._timeSinceFrameUpdate = BABYLON.Tools.Now;
    }

    private _checkLimits() {
        if (this.lowerBetaLimit === null || this.lowerBetaLimit === undefined) {
            if (this.allowUpsideDown && this.beta > Math.PI) {
                this.beta = this.beta - (2 * Math.PI);
            }
        } else {
            if (this.beta < this.lowerBetaLimit) {
                this.beta = this.lowerBetaLimit;
            }
        }

        if (this.upperBetaLimit === null || this.upperBetaLimit === undefined) {
            if (this.allowUpsideDown && this.beta < -Math.PI) {
                this.beta = this.beta + (2 * Math.PI);
            }
        } else {
            if (this.beta > this.upperBetaLimit) {
                this.beta = this.upperBetaLimit;
            }
        }

        if (this.lowerAlphaLimit && this.alpha < this.lowerAlphaLimit) {
            this.alpha = this.lowerAlphaLimit;
        }
        if (this.upperAlphaLimit && this.alpha > this.upperAlphaLimit) {
            this.alpha = this.upperAlphaLimit;
        }

        if (this.lowerRadiusLimit && this.radius < this.lowerRadiusLimit) {
            this.radius = this.lowerRadiusLimit;
        }
        if (this.upperRadiusLimit && this.radius > this.upperRadiusLimit) {
            this.radius = this.upperRadiusLimit;
        }
    }

    public setPosition(position: BABYLON.Vector3): void {
        if (this.mode == BABYLON.Camera.PERSPECTIVE_CAMERA) {
            var radiusv3 = position.subtract(this._getTargetPosition());
            this.radius = radiusv3.length();
            // Alpha
            this.alpha = Math.acos(radiusv3.x / Math.sqrt(Math.pow(radiusv3.x, 2) + Math.pow(radiusv3.z, 2)));
            if (radiusv3.z < 0) {
                this.alpha = 2 * Math.PI - this.alpha;
            }
            // Beta
            this.beta = Math.acos(radiusv3.y / this.radius);

            if (isNaN(this.alpha)) {
                this.alpha = 0;
            }

            if (isNaN(this.beta)) {
                this.beta = 0;
            }

            this.position = position;
        }
        else if (this.mode == BABYLON.Camera.ORTHOGRAPHIC_CAMERA) {

            //position = this.position;
            var radiusv3 = position.subtract(this._getTargetPosition());
            this.radius = radiusv3.length();
            // Alpha
            this.alpha = Math.acos(radiusv3.x / Math.sqrt(Math.pow(radiusv3.x, 2) + Math.pow(radiusv3.z, 2)));
            if (radiusv3.z < 0) {
                this.alpha = 2 * Math.PI - this.alpha;
            }
            // Beta
            this.beta = Math.acos(radiusv3.y / this.radius);

            if (isNaN(this.alpha)) {
                this.alpha = 0;
            }

            if (isNaN(this.beta)) {
                this.beta = 0;
            }

            this.position = position;
        }

        this._checkLimits();
    }

    public updateZoomSpeed(bounds: BABYLON.BoundingBox) {

        if ((bounds != null) && (bounds.minimum != null) && (bounds.maximum != null)) {
            var xSize = bounds.maximum.x - bounds.minimum.x;
            var ySize = bounds.maximum.y - bounds.minimum.y;
            var zSize = bounds.maximum.z - bounds.minimum.z;

            var area = xSize * zSize;

            var aS = Math.sqrt(area);
            this._sceneExtentZoomFactor = Math.max(1, aS / 50);
        }
    }

    public flyCameraAndTarget(): void {
        var direction = this.position.subtract(this.target).normalize();
        this.target = this.target.add(direction.scale(this.flyZoomFactor));
        this.position = this.position.add(direction.scale(this.flyZoomFactor));
    }

    public isMoving(): boolean {
        return this._moving || this.inertialPanningX != 0 || this.inertialPanningY != 0 || this.inertialAlphaOffset != 0 || this.inertialBetaOffset != 0 || this.inertialRadiusOffset != 0 || this.radiusVelocity != 0;
    }
    public _getViewMatrix(): BABYLON.Matrix {
        // Compute
        var cosa = Math.cos(this.alpha);
        var sina = Math.sin(this.alpha);
        var cosb = Math.cos(this.beta);
        var sinb = Math.sin(this.beta);

        var target = this._getTargetPosition();
        target.addToRef(new BABYLON.Vector3(this.radius * cosa * sinb, this.radius * cosb, this.radius * sina * sinb), this._newPosition);
        if (this.getScene().collisionsEnabled && this.checkCollisions) {
            this._collider._radius = this.collisionRadius;
            this._newPosition.subtractToRef(this.position, this._collisionVelocity);
            this._collisionTriggered = true;
            this.getScene().collisionCoordinator.getNewPosition(this.position, this._collisionVelocity, this._collider, 3, null, null, this.uniqueId);
        } else {
            this.position.copyFrom(this._newPosition);

            var up = this.upVector;
            if (this.allowUpsideDown && this.beta < 0) {
                var up = up.clone();
                up = up.negate();
            }

            BABYLON.Matrix.LookAtLHToRef(this.position, target, up, this._viewMatrix);

            this._viewMatrix.addAtIndex(12, this.targetScreenOffset.x);
            this._viewMatrix.addAtIndex(13, this.targetScreenOffset.y);
        }

        var cameraDirection = target.subtract(this.position).normalize();
        var directionVector = new BABYLON.Vector3(cameraDirection.x, 0, cameraDirection.z).normalize();

        var upDotProduct = BABYLON.Vector3.Dot(cameraDirection, directionVector);
        var directionDotProduct = BABYLON.Vector3.Dot(directionVector, new BABYLON.Vector3(0, 0, 1));
        upDotProduct = upDotProduct > 1 ? 1 : upDotProduct;
        directionDotProduct = directionDotProduct > 1 ? 1 : directionDotProduct;

        upDotProduct = upDotProduct > 1 ? 1 : upDotProduct;
        directionDotProduct = directionDotProduct > 1 ? 1 : directionDotProduct;

        var angleUp = Math.acos(upDotProduct);
        var angleFromNorth = Math.acos(directionDotProduct);

        // If the X is negative, then the rotation is 360 degrees MINUS the angle.
        angleFromNorth = directionVector.x < 0 ? 2 * Math.PI - angleFromNorth : angleFromNorth;

        var upAxis = Math.round(angleUp * 180 / (Math.PI)) == 0 ? "" : cameraDirection.y > 0 ? "U" : "D";

        var rotatedDegrees = Math.round(angleFromNorth * 180 / (Math.PI)) == 360 ? 0 : Math.round(angleFromNorth * 180 / (Math.PI));
        var upDegrees = angleUp * 180 / (Math.PI) > 89 ? 90 : Math.round(angleUp * 180 / (Math.PI));

        var args: any = {
            rotatedDegrees: rotatedDegrees,
            upDegrees: upDegrees + "° " + upAxis,
            pos: this.position,
            target: target
        };
        this._eventHandler.fireEvent(Enumerations.EventType.CAMERACHANGED, args);

        if (this._headlight) {

            var cosa = Math.cos(this.alpha);
            var sina = Math.sin(this.alpha);
            var cosb = Math.cos(this.beta);
            var sinb = Math.sin(this.beta);

            var lightPosition = this.position.clone();
            this.target.addToRef(new BABYLON.Vector3(this.radius * cosa * sinb, this.radius * cosb, this.radius * sina * sinb), lightPosition);

            this._headlight.direction = this.target.subtract(lightPosition).normalize();
            this._headlight.position = lightPosition;
        }

        this.computeViewMatrix();
        return this._viewMatrix;
    }
    public computeViewMatrix(force?: boolean) {
        this._computeViewMatrix(this.position, this.target, this.upVector);
    }

    private _onCollisionPositionChange = (collisionId: number, newPosition: BABYLON.Vector3, collidedMesh: BABYLON.AbstractMesh = null) => {

        if (this.checkCollisions) {
            newPosition.multiplyInPlace(this._collider._radius);
        }

        if (!collidedMesh) {
            this._previousPosition.copyFrom(this.position);
        } else {
            this.setPosition(this.position);

            if (this.onCollide) {
                this.onCollide(collidedMesh);
            }
        }

        // Recompute because of constraints
        var cosa = Math.cos(this.alpha);
        var sina = Math.sin(this.alpha);
        var cosb = Math.cos(this.beta);
        var sinb = Math.sin(this.beta);
        var target = this._getTargetPosition();
        target.addToRef(new BABYLON.Vector3(this.radius * cosa * sinb, this.radius * cosb, this.radius * sina * sinb), this._newPosition);
        this.position.copyFrom(this._newPosition);

        var up = this.upVector;
        if (this.allowUpsideDown && this.beta < 0) {
            var up = up.clone();
            up = up.negate();
        }

        BABYLON.Matrix.LookAtLHToRef(this.position, target, up, this._viewMatrix);
        var newRow = new BABYLON.Vector4(this.targetScreenOffset.x, this.targetScreenOffset.y, this._viewMatrix.m[14], this._viewMatrix.m[15]);
        this.getViewMatrix().setRow(3, newRow);

        this._collisionTriggered = false;
    }

    public zoomOn(meshes?: BABYLON.AbstractMesh[], doNotUpdateMaxZ = false): void {
        meshes = meshes || this.getScene().meshes;

        var minMaxVector = BABYLON.Mesh.MinMax(meshes);
        var distance = BABYLON.Vector3.Distance(minMaxVector.min, minMaxVector.max);

        this.radius = distance * this.zoomOnFactor;

        this.focusOn({ min: minMaxVector.min, max: minMaxVector.max, distance: distance }, doNotUpdateMaxZ);
    }

    public focusOn(meshesOrMinMaxVectorAndDistance: any, doNotUpdateMaxZ = false): void {
        var meshesOrMinMaxVector: any;
        var distance: any;

        if (meshesOrMinMaxVectorAndDistance.min === undefined) { // meshes
            meshesOrMinMaxVector = meshesOrMinMaxVectorAndDistance || this.getScene().meshes;
            meshesOrMinMaxVector = BABYLON.Mesh.MinMax(meshesOrMinMaxVector);
            distance = BABYLON.Vector3.Distance(meshesOrMinMaxVector.min, meshesOrMinMaxVector.max);
        }
        else { //minMaxVector and distance
            meshesOrMinMaxVector = meshesOrMinMaxVectorAndDistance;
            distance = meshesOrMinMaxVectorAndDistance.distance;
        }

        this.target = BABYLON.Mesh.Center(meshesOrMinMaxVector);

        if (!doNotUpdateMaxZ) {
            this.maxZ = distance * 2;
        }
    }

    /**
     * @override
     * Override Camera.createRigCamera
     */
    public createRigCamera(name: string, cameraIndex: number): BABYLON.Camera {
        switch (this.cameraRigMode) {
            case BABYLON.Camera.RIG_MODE_STEREOSCOPIC_ANAGLYPH:
            case BABYLON.Camera.RIG_MODE_STEREOSCOPIC_SIDEBYSIDE_PARALLEL:
            case BABYLON.Camera.RIG_MODE_STEREOSCOPIC_SIDEBYSIDE_CROSSEYED:
            case BABYLON.Camera.RIG_MODE_STEREOSCOPIC_OVERUNDER:
            case BABYLON.Camera.RIG_MODE_VR:
                var alphaShift = this._cameraRigParams.stereoHalfAngle * (cameraIndex === 0 ? 1 : -1);
                return new ExamineCamera(name, this._eventHandler, this.alpha + alphaShift, this.beta, this.radius, this.target, this.getScene());
        }
    }

    /**
     * @override
     * Override Camera._updateRigCameras
     */
    public _updateRigCameras() {
        switch (this.cameraRigMode) {
            case BABYLON.Camera.RIG_MODE_STEREOSCOPIC_ANAGLYPH:
            case BABYLON.Camera.RIG_MODE_STEREOSCOPIC_SIDEBYSIDE_PARALLEL:
            case BABYLON.Camera.RIG_MODE_STEREOSCOPIC_SIDEBYSIDE_CROSSEYED:
            case BABYLON.Camera.RIG_MODE_STEREOSCOPIC_OVERUNDER:
            case BABYLON.Camera.RIG_MODE_VR:
                var camLeft = <ExamineCamera>this._rigCameras[0];
                var camRight = <ExamineCamera>this._rigCameras[1];
                camLeft.alpha = this.alpha - this._cameraRigParams.stereoHalfAngle;
                camRight.alpha = this.alpha + this._cameraRigParams.stereoHalfAngle;
                camLeft.beta = camRight.beta = this.beta;
                camLeft.radius = camRight.radius = this.radius;
                break;
        }
        super._updateRigCameras();
    }

    private updateViewport() {

        var timeDiff = (BABYLON.Tools.Now - this._timeSinceFrameUpdate) / 1000;
        if (timeDiff > 1)
            timeDiff = 1;

        var radiusDeltaPerSecondSpeed = this.radiusVelocity * timeDiff;
        var radiusShift = this.inertialRadiusOffset * this.zoomFactor;

        if (this.radius - radiusShift < .001) {
            this.radius = .001 * this.zoomFactor;
            this.orthoBottom = this.radius * this._radiusToOrthoScale * -1;
            this.orthoLeft = this.radius * this._radiusToOrthoScale * this.widthScale * -1;
            this.orthoRight = this.radius * this._radiusToOrthoScale * this.widthScale;
            this.orthoTop = this.radius * this._radiusToOrthoScale;
        }
        else {

            this.radius -= radiusShift;
            this.orthoBottom = this.radius * this._radiusToOrthoScale * -1;
            this.orthoLeft = this.radius * this._radiusToOrthoScale * this.widthScale * -1;
            this.orthoRight = this.radius * this._radiusToOrthoScale * this.widthScale;
            this.orthoTop = this.radius * this._radiusToOrthoScale;
        }

        if (radiusDeltaPerSecondSpeed != 0) {
            if (this.radius - radiusDeltaPerSecondSpeed < .001) {
                this.radius = .001 * this.zoomFactor;
                this.orthoBottom = this.radius * this._radiusToOrthoScale * -1;
                this.orthoLeft = this.radius * this._radiusToOrthoScale * this.widthScale * -1;
                this.orthoRight = this.radius * this._radiusToOrthoScale * this.widthScale;
                this.orthoTop = this.radius * this._radiusToOrthoScale;
            }
            else {

                this.radius -= radiusDeltaPerSecondSpeed;
                this.orthoBottom = this.radius * this._radiusToOrthoScale * -1;
                this.orthoLeft = this.radius * this._radiusToOrthoScale * this.widthScale * -1;
                this.orthoRight = this.radius * this._radiusToOrthoScale * this.widthScale;
                this.orthoTop = this.radius * this._radiusToOrthoScale;
            }
        }


    }

    /**
     * Temporarily suspend camera interactive controls (pointer rotate, pan, zoom, wheel, touch).
     * Call with true on gizmo drag start and false on drag end.
     */
    public setControlsSuspended(suspended: boolean, resetInertia: boolean = true) {
        this._suspendControls = suspended;
        if (suspended && resetInertia) {
            // Clear ongoing inertial movement so camera stays still while gizmo operates
            this.inertialAlphaOffset = 0;
            this.inertialBetaOffset = 0;
            this.inertialRadiusOffset = 0;
            this.inertialPanningX = 0;
            this.inertialPanningY = 0;
            this.radiusVelocity = 0;
        }
    }

    public setVelocitySensitivity(sensitivity: number) {
        this.velocitySensitivityPos = sensitivity;
    }

    public setRotationSensitivity(sensitivity: number) {
        this.rotationSensitivityPos = sensitivity;
    }

    public dollyIncrement(increment: number) {
        const velMul = this._velocityMultiplier();

        this.inertialRadiusOffset = increment * velMul;
    }
    public setRadiusVelocity(velocity: number) {
        const velMul = this._velocityMultiplier();

        this.radiusVelocity = velocity * velMul;
    }
    public setInvertY(inverted: boolean) {
        this.invertY = inverted;
    }
    private _rotationMultiplier(): number {
        // -1 => 0.5x, 0 => 1x, +1 => 2x
        return Math.pow(2, this.rotationSensitivityPos);
    }

    private _velocityMultiplier(): number {
        // -1 => 0.5x, 0 => 1x, +1 => 5x
        const s = this.velocitySensitivityPos;
        return s < 0 ? Math.pow(2, s) : Math.pow(5, s);
    }
}

export default ExamineCamera;